// Catch Charm: a timed boost, +25% catch chance on every seal the trainer throws on the hunt (inside the usual
// 0.3%-10% clamp, shared/capture.ts). The Aura Charm's sibling, in the same style: one clock on the trainer's boosts
// (catchUntil), extended from where it stands.
//
// Both charms are won in ranked seasons (shared/ranked.ts) as Bag items (shared/items.ts charmItem, 1 h and 24 h).
// Until used they are ordinary tradeable items, listed and bought on the player market with the usual fees; using one
// from the Bag starts or extends the boost on the user, and the running boost is account state. An Aura Charm item adds
// to the free Aura clock (shared/auracharm.ts), after any Aura Charm already running. The two never run together: an
// Aura Charm item can't start while a Catch Charm runs, nor a Catch Charm item while any Aura Charm runs, which keeps
// the Aura supply they add inside the eval band (sim/targets.ts auraRankedAll).
import type { TrainerDTO } from './protocol.ts';
import { charmUntil } from './auracharm.ts';

export const CATCH_CHARM = { mult: 1.25 } as const;
type Boosts = TrainerDTO['boosts'];

/** When the Catch Charm ends (0 when none runs). */
export function catchCharmUntil(b: Boosts | undefined, now: number): number {
  const until = b?.catchUntil ?? 0;
  return until > now ? until : 0;
}

/** The catch chance multiplier at `now`: ×1.25 while a Catch Charm runs. */
export function catchCharmMult(b: Boosts | undefined, now: number): number {
  return catchCharmUntil(b, now) ? CATCH_CHARM.mult : 1;
}

/** Add `ms` of Catch Charm, from the end of what already runs (or from now). */
export function extendCatchCharm(b: Boosts, ms: number, now: number): void {
  if (!(ms > 0)) return;
  b.catchUntil = Math.max(now, b.catchUntil ?? 0) + ms;
}

/** "+25% catch chance", the one wording every boost list uses. */
export const catchCharmEffect = () => `+${Math.round((CATCH_CHARM.mult - 1) * 100)}% catch chance`;

/** Why a charm item can't be used now (the other kind runs), or null when it can. */
export function charmItemBlocked(b: Boosts | undefined, charm: 'aura' | 'catch', now: number): string | null {
  if (charm === 'aura' && catchCharmUntil(b, now)) return 'A Catch Charm is running. Aura and Catch Charms take turns: use this one when it ends.';
  if (charm === 'catch' && charmUntil(b, now)) return 'An Aura Charm is running. Aura and Catch Charms take turns: use this one when it ends.';
  return null;
}

/**
 * Use a charm item: an Aura Charm adds to the free Aura clock after whatever Aura Charm runs (a bought one included, so
 * no hour is lost under it); a Catch Charm extends the Catch clock. Returns an error when the other kind runs.
 */
export function useCharmItem(b: Boosts, boost: { charm: 'aura' | 'catch'; hours: number }, now: number): string | null {
  const why = charmItemBlocked(b, boost.charm, now);
  if (why) return why;
  const ms = boost.hours * 3_600_000;
  if (!(ms > 0)) return 'That charm is empty.';
  if (boost.charm === 'aura') b.auraFreeUntil = Math.max(now, charmUntil(b, now)) + ms;
  else extendCatchCharm(b, ms, now);
  return null;
}
